import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { INDUSTRIES, CUSTOMER_STATUS } from '@/modules/Customer/constants'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { Filter, Loader2, Search } from 'lucide-react'
import { Control, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo } from 'react'
import { Button, FormItem, Typogaphy } from 'vnetwork-ui'
import {
    getInternalVariablesByType,
    MAILER_INTERNAL_TYPES,
    MailerInternalType,
    SERVICE_STATUS_OPTIONS,
    CUSTOMER_STATUS_OPTIONS,
    REQUIRED_FIELD_OPTIONS,
} from '../../../types'
import { FormValues } from '../hooks/useCampaignForm'
import { DEPARTMENT_IDS } from '@/constants/departments'

interface InternalFiltersSectionProps {
    control: Control<FormValues>
    isPreviewingFilters: boolean
    onPreviewFilters: (page?: number, search?: string) => void
}

// Status groups for conditional logic
const SERVICE_RELATED_STATUSES = [
    CUSTOMER_STATUS.ACTIVE,
    CUSTOMER_STATUS.TRIAL,
    CUSTOMER_STATUS.STOP,
]
const POTENTIAL_CUSTOMER_STATUS = CUSTOMER_STATUS.COOLLEAD

const InternalFiltersSection: React.FC<InternalFiltersSectionProps> = ({
    control,
    isPreviewingFilters,
    onPreviewFilters,
}) => {
    const { t } = useTranslation()
    const { setValue } = useFormContext<FormValues>()

    // Watch form values
    const internalType = useWatch({
        control,
        name: 'mailer_campaign_internal_type',
    })
    const filters = useWatch({ control, name: 'mailer_campaign_filters' })
    const customerStatus = filters?.customer_status || []

    // Conditional visibility logic
    const visibility = useMemo(() => {
        // Count how many of active/trial/stop are selected
        const serviceRelatedCount = customerStatus.filter((status) =>
            SERVICE_RELATED_STATUSES.includes(status)
        ).length

        // Check if coollead (potential customer) is selected
        const hasPotentialCustomer = customerStatus.includes(
            POTENTIAL_CUSTOMER_STATUS
        )

        return {
            // Show product_group_name if any of active/trial/stop is selected
            showProductGroup: serviceRelatedCount > 0,
            // Show service_status if 2+ of active/trial/stop are selected
            showServiceStatus: serviceRelatedCount >= 2,
            // Show service_interestings if coollead is selected
            showServiceInterestings: hasPotentialCustomer,
        }
    }, [customerStatus])

    // Clear fields when conditions are no longer met
    useEffect(() => {
        if (
            !visibility.showProductGroup &&
            filters?.product_group_name?.length
        ) {
            setValue('mailer_campaign_filters.product_group_name', [])
        }
    }, [
        visibility.showProductGroup,
        filters?.product_group_name?.length,
        setValue,
    ])

    useEffect(() => {
        if (!visibility.showServiceStatus && filters?.service_status?.length) {
            setValue('mailer_campaign_filters.service_status', [])
        }
    }, [
        visibility.showServiceStatus,
        filters?.service_status?.length,
        setValue,
    ])

    useEffect(() => {
        if (
            !visibility.showServiceInterestings &&
            filters?.service_interestings?.length
        ) {
            setValue('mailer_campaign_filters.service_interestings', [])
        }
    }, [
        visibility.showServiceInterestings,
        filters?.service_interestings?.length,
        setValue,
    ])

    // Fetch product groups from API
    const {
        data: productGroupOptions = [],
        isLoading: isLoadingProductGroups,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCT_GROUPS, 'options'],
        queryFn: () => getProductsGroupOptions(),
    })

    // Options
    const industryOptions = INDUSTRIES.map((industry) => ({
        value: industry.value,
        label: industry.label,
    }))

    const customerStatusOptions = CUSTOMER_STATUS_OPTIONS(t).map((opt) => ({
        value: opt.value,
        label: opt.label,
    }))

    const serviceStatusOptions = SERVICE_STATUS_OPTIONS(t).map((opt) => ({
        value: opt.value,
        label: opt.label,
    }))

    const internalTypeOptions = MAILER_INTERNAL_TYPES.map((opt) => ({
        value: opt.value,
        label: t(opt.value) || opt.label,
    }))

    const requiredFieldOptions = REQUIRED_FIELD_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }))

    // Check if any filters are set
    const hasFilters = () => {
        return (
            (filters?.customer_status?.length ?? 0) > 0 ||
            (filters?.customer_industry?.length ?? 0) > 0 ||
            (filters?.product_group_name?.length ?? 0) > 0 ||
            (filters?.service_status?.length ?? 0) > 0 ||
            (filters?.service_interestings?.length ?? 0) > 0 ||
            (filters?.customer_sale_followers?.length ?? 0) > 0
        )
    }

    return (
        <div className="p-3 border rounded-lg border-blue-200 bg-blue-50/30">
            <div className="flex items-center gap-2 mb-3">
                <Filter size={14} className="text-blue-600" />
                <Typogaphy className="text-sm font-medium text-blue-700">
                    {t('filter_customers')}
                </Typogaphy>
            </div>

            {/* Filters Grid - 2 columns */}
            <div className="grid grid-cols-2 gap-3">
                {/* Internal Type */}
                <FormItem
                    control={control}
                    name="mailer_campaign_internal_type"
                    type="select"
                    label={t('mailerCampaign:data_source_type')}
                    placeholder={t('mailerCampaign:select_data_source_type')}
                    options={internalTypeOptions}
                    disabled
                    allowClear={false}
                    size="small"
                />

                {/* Customer Status */}
                <FormItem
                    control={control}
                    name="mailer_campaign_filters.customer_status"
                    type="select"
                    label={t('customer_status')}
                    placeholder={t('select_customer_status')}
                    options={customerStatusOptions}
                    multiple
                    size="small"
                />

                {/* Customer Industry */}
                <FormItem
                    control={control}
                    name="mailer_campaign_filters.customer_industry"
                    type="select"
                    label={t('customer_industry')}
                    placeholder={t('select_customer_industry')}
                    options={industryOptions}
                    multiple
                    size="small"
                />

                {/* Product Group - Only show if active/trial/stop selected */}
                {visibility.showProductGroup && (
                    <FormItem
                        control={control}
                        name="mailer_campaign_filters.product_group_name"
                        type="select"
                        label={t('product_group')}
                        placeholder={t('select_product_group')}
                        options={productGroupOptions}
                        multiple
                        loading={isLoadingProductGroups}
                        size="small"
                    />
                )}

                {/* Service Status - Only show if 2+ of active/trial/stop selected */}
                {visibility.showServiceStatus && (
                    <FormItem
                        control={control}
                        name="mailer_campaign_filters.service_status"
                        type="select"
                        label={t('service_status')}
                        placeholder={t('select_service_status')}
                        options={serviceStatusOptions}
                        multiple
                        size="small"
                    />
                )}

                {/* Service Interestings - Only show if coollead (potential customer) selected */}
                {visibility.showServiceInterestings && (
                    <FormItem
                        control={control}
                        name="mailer_campaign_filters.service_interestings"
                        type="select"
                        label={t('service_interestings')}
                        placeholder={t(
                            'mailerCampaign:select_service_interestings'
                        )}
                        options={productGroupOptions}
                        multiple
                        loading={isLoadingProductGroups}
                        size="small"
                    />
                )}

                {/* Sale Followers - Full width */}
                <div className="col-span-2">
                    <EmployeeSelect
                        control={control}
                        name="mailer_campaign_filters.customer_sale_followers"
                        multiple
                        label="sale_followers"
                        departmentIds={[
                            DEPARTMENT_IDS.ACCOUNT,
                            DEPARTMENT_IDS.SALES,
                            DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                        ]}
                    />
                </div>
                {/* is_vn - Customer language */}
                <div className="col-span-2">
                    <FormItem
                        control={control}
                        name="mailer_campaign_filters.is_vn"
                        type="select"
                        label={t('customer_language')}
                        placeholder={t(
                            'mailerCampaign:select_customer_language'
                        )}
                        options={[
                            {
                                value: true,
                                label: t('mailerCampaign:customer_language_vn'),
                            },
                            {
                                value: false,
                                label: t(
                                    'mailerCampaign:customer_language_not_vn'
                                ),
                            },
                        ]}
                        size="small"
                    />
                </div>
                {/* Required Fields - Full width */}
                <div className="col-span-2">
                    <FormItem
                        control={control}
                        name="mailer_campaign_filters.required_fields"
                        type="select"
                        label={t('required_fields')}
                        placeholder={t('select_required_fields')}
                        options={requiredFieldOptions}
                        multiple
                        size="small"
                        helperText={t('required_fields_helper')}
                    />
                </div>
            </div>

            {/* Preview Button & Variables - Inline */}
            <div className="flex items-start justify-between gap-4 mt-3 pt-3 border-t border-blue-200">
                <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => onPreviewFilters()}
                    disabled={!hasFilters() || isPreviewingFilters}
                    startIcon={
                        isPreviewingFilters ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Search size={14} />
                        )
                    }
                >
                    {t('preview_results')}
                </Button>

                {/* Available Variables - Compact */}
                <div className="flex-1">
                    <Typogaphy className="text-xs font-medium text-green-700 mb-1">
                        {t('mailerCampaign:available_variables')}:
                    </Typogaphy>
                    <div className="flex flex-wrap gap-1">
                        {/* Email variable (mapped to customer_email) */}
                        <code
                            className="px-1.5 py-0.5 bg-blue-100 border border-blue-300 rounded text-[10px] text-blue-800 font-mono"
                            title="Email (= customer_email)"
                        >
                            {`{{email}}`}
                        </code>
                        {getInternalVariablesByType(
                            internalType as MailerInternalType
                        ).map((variable) => (
                            <code
                                key={variable.name}
                                className="px-1.5 py-0.5 bg-green-100 border border-green-300 rounded text-[10px] text-green-800 font-mono"
                                title={variable.label}
                            >
                                {`{{${variable.name}}}`}
                            </code>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default InternalFiltersSection
